import { useEffect, useState, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { adminGet, adminPostBlob, adminGetBlob, downloadBlob } from './adminApi';
import { Button } from '@/components/ui/button';
import { QrCode } from 'lucide-react';
import { toast } from 'sonner';

const mono = { fontFamily: "'IBM Plex Mono', monospace" };

function QrPreview({ deviceId }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    if (!deviceId) return undefined;
    adminGetBlob(`/qr/devices/${deviceId}/preview.png`).then(blob => {
      url = URL.createObjectURL(blob);
      setSrc(url);
    }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [deviceId]);
  if (!src) return <QrCode className="w-12 h-12" />;
  return <img src={src} alt="Device QR" className="w-full h-full object-contain" data-testid="device-qr-preview-img" />;
}

// Phase 7 Devices → Device labels: the 50×30 ID-only device QR (split out of the old QR labels screen).
export default function DeviceLabelsPage() {
  const [customers, setCustomers] = useState([]);
  const [tenantId, setTenantId] = useState('');          // '' = Track0tic stock (unallocated devices)
  const [labels, setLabels] = useState([]);
  const { role } = useOutletContext() || {};
  const canPrint = !role || role === 'platform_admin' || role === 'assembly_tech';
  const customerName = customers.find(c => c.id === tenantId)?.name;

  const loadDevices = useCallback(() => {
    adminGet(`/qr/devices${tenantId ? `?tenant_id=${tenantId}` : ''}`).then(setLabels).catch(e => toast.error(e.message));
  }, [tenantId]);
  useEffect(() => { adminGet('/customers-lite').then(setCustomers).catch(() => {}); }, []);
  useEffect(() => { loadDevices(); }, [loadDevices]);

  const printLabel = async (l) => {
    let reason = null;
    if (l.printed) {
      reason = window.prompt('Reprint reason (damaged, lost, other):', 'damaged');
      if (!reason) return;
    }
    try {
      const blob = await adminPostBlob(`/qr/devices/${l.id}/print`, { reason });
      downloadBlob(blob, `${l.trackotic_id}-label.pdf`);
      toast.success(l.printed ? 'Reprint logged and downloaded' : 'Label printed');
      loadDevices();
    } catch (e) { toast.error(e.message); }
  };

  const firstDevice = labels[0];

  return (
    <div className="p-8 max-w-7xl space-y-6" data-testid="device-labels-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold" style={{ color: '#1B1D1C' }}>Device labels</h1>
          <p className="text-sm mt-1" style={{ color: '#6b7069' }}>Permanent 50×30 mm QR on the logger — encodes the device token (/d/… link) with the Track0tic ID printed beneath. Passport labels live under Sale &amp; supply → Issue passports.</p>
        </div>
        <div>
          <label className="text-xs block mb-1" style={{ color: '#6b7069' }}>Customer</label>
          <select data-testid="qr-customer-select" value={tenantId} onChange={e => setTenantId(e.target.value)}
            className="h-9 rounded border px-3 text-sm bg-white min-w-[260px]" style={{ borderColor: '#D9D6CD' }}>
            <option value="">Track0tic stock (unallocated devices)</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      </div>

      {/* Card 1 — Device QR */}
      <div className="rounded-lg border bg-white p-6" style={{ borderColor: '#E5E3DC' }} data-testid="device-qr-card">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xl font-bold" style={{ color: '#1B1D1C' }}>Device QR · on the logger</h2>
          <span className="px-2.5 py-1 rounded text-xs" style={{ background: '#E7F2EE', color: '#0B6B55' }}>Permanent</span>
        </div>
        <p className="text-sm mb-5" style={{ color: '#6b7069' }}>
          {tenantId ? `Devices allocated to ${customerName}.` : 'Devices in Track0tic stock.'} One label per device, encodes a device token (the /d/… link), not the Track0tic ID. Never re-issued: a reprint carries the same token.
        </p>
        {firstDevice && (
          <div className="rounded-md p-5 mb-5 flex items-center gap-5" style={{ background: '#FAF9F6', border: '1px solid #EDEBE4' }}>
            <div className="w-24 h-24 border-2 rounded flex items-center justify-center bg-white p-1" style={{ borderColor: '#1B1D1C' }}>
              <QrPreview deviceId={firstDevice.id} />
            </div>
            <div>
              <p className="text-2xl font-bold" style={mono}>{firstDevice.trackotic_id}</p>
              <p className="text-sm mt-1" style={{ color: '#6b7069' }}>High error correction · weatherproof label · ID printed beside QR</p>
            </div>
          </div>
        )}
        <table className="w-full text-sm">
          <thead><tr className="text-left border-b" style={{ color: '#6b7069', borderColor: '#EDEBE4' }}>
            <th className="py-2 font-medium">Track0tic ID</th><th className="font-medium">Type</th>
            <th className="font-medium">Printed</th><th className="font-medium">Reprints</th><th className="font-medium"></th></tr></thead>
          <tbody>
            {labels.map(l => (
              <tr key={l.id} className="border-b" style={{ borderColor: '#F3F1EA' }} data-testid={`device-label-row-${l.trackotic_id}`}>
                <td className="py-2.5" style={mono}>{l.trackotic_id}</td>
                <td>{l.device_type_display}</td>
                <td style={{ color: '#6b7069' }}>{l.printed_at ? new Date(l.printed_at).toLocaleDateString('en-IN') : '—'}</td>
                <td style={mono}>{l.reprints}</td>
                <td className="text-right">
                  {canPrint && (
                    <Button size="sm" variant="outline" data-testid={`print-label-${l.trackotic_id}`} onClick={() => printLabel(l)}
                      className="bg-white border-[#D9D6CD]">{l.printed ? 'Reprint' : 'Print'}</Button>
                  )}
                </td>
              </tr>
            ))}
            {labels.length === 0 && <tr><td colSpan={5} className="py-6 text-center" style={{ color: '#9a9e96' }} data-testid="device-labels-empty">
              {tenantId ? 'No devices with a Track0tic ID allocated to this customer' : 'No devices with a Track0tic ID in stock'}</td></tr>}
          </tbody>
        </table>
        <p className="text-xs mt-4" style={{ color: '#9a9e96' }}>Every reprint is logged with who and why (damaged, lost label).</p>
      </div>

    </div>
  );
}
